import {batch} from 'react-redux';
import {isEqual} from 'lodash';
import {showModalType} from 'stores/actions/typedModalAction';
import {resetManualReteachPlateState} from 'stores/actions/reteachPlateAction';
import {InstrumentProcessType} from 'constants/instrumentStatusEnum';
import {selectIsPlateRecognitionEnabled} from 'stores/selectors/plateRecognitionSelectors';
import FormattedHTMLMessage from '../../components/FormattedHTMLMessage';
import {
  CommonButtonLabel,
  FastRobotErrorcode,
  Feedback,
  ModalType,
  PlateType,
  SetTrayModalType,
  TrayType,
  ViewMode,
  WellBottomProfile,
  WellCount,
  WellSurfaceDataShape,
} from '../../constants/dataEnum';
import {WellLabel} from '../../contents/PlateTypeEditor/PlateTypeEditorEnum';
import {changeView, hideLoading, resetIsManualReteachPage, showLoading, showSnackbar} from '../actions/appStateAction';
import {hideModal, showModal} from '../actions/modalStateAction';
import {
  decrementCurrentWizardView,
  incrementCurrentWizardView,
  resetPlateTypeEditorState,
  setCapacity,
  setColumnOrigin,
  setCurrentWizardView,
  setDepth,
  setHeight,
  setLastProcessedId,
  setLength,
  setPlateTypeEditorEditMode,
  setPlateTypeEditorEjectTray,
  setProcessedStep,
  setRowOrigin,
  setTroughDepth,
  setWidth,
  setWorkingVolume,
} from '../actions/plateTypeEditorStateAction';
import {removeTemporaryState} from '../actions/temporaryStateAction';
import {resolveIntl, setErrorCodeNumber} from './commonBusiness';
import {showConfirmationModal, showModalUnsupportedTray} from './commonModalBusiness';
// eslint-disable-next-line import/no-cycle
import {fastCommonActuatorAPIErrorHandler} from './errorHandlerBusiness';
import {
  alignToDeckInsertWithOffset,
  ejectTray,
  getCurrentPosition,
  isTouchProbeExpanded,
  isTrayInstalled,
  measureWell,
  safeMoveVerticalCarrier,
  safeTouchProbeExtend,
  safeTouchProbeRetract,
  moveToIdlePosition as moveToIdlePositionApi,
} from './instrumentBusiness';
import {createLabwareDefinitions, refreshLabwareDefinitions, updateLabwareDefinition} from './labwareDatabaseBusiness';
import {checkIsTouchProbeExtendedWithModal} from './modalStateBusinessAction';
// eslint-disable-next-line import/no-cycle
import {fastRecovery} from './recoveryBusiness';
import {saveState} from './stateBusiness';
import {redirectToReteachPlatePage} from './reteachPlateBusiness';
import {showLoadCellErrorModal} from './loadCellErrorBusiness';
import {checkTrayReadiness} from './trayCheckingBusiness';
import {onEnterCalibrationMenuErrorHandler} from './menuBusiness';
import {
  checkAndHandleIsAllowedToStartProcess,
  clearInstrumentProcess,
  setInstrumentProcess,
} from './instrumentStatusBusiness';
import {registerCachedAndClearScannedData} from './plateRecognitionBusiness';
import {retractTouchProbe} from './trayCalibrationBusiness';

const intl = resolveIntl();

const getWellLabelFromWellNumber = (wellNumber) =>
  wellNumber === WellCount.WELL_IFC_192_24 ? '192.24' : wellNumber.toString();

export const handleResetProbePosition =
  (startingPosition, succesCallback, errorHandlerCallback) => (dispatch, getState) => {
    const {plateTypeEditorState, configState} = getState();
    const {wellNumber, wellLabel} = plateTypeEditorState;
    const {plateTypeEditorDeckInsert} = configState;
    dispatch(showLoading(intl.formatMessage({id: 'plateTypeEditorContainer.preparingPosition.loading'})));

    dispatch(
      alignToDeckInsertWithOffset(
        plateTypeEditorDeckInsert,
        wellLabel || getWellLabelFromWellNumber(wellNumber),
        startingPosition,
      ),
    ).then((response) => {
      dispatch(hideLoading());
      if (response !== FastRobotErrorcode.NoError) {
        errorHandlerCallback(response, () => {
          dispatch(handleResetProbePosition(startingPosition, succesCallback, errorHandlerCallback));
        });
        return;
      }
      dispatch(
        showSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({
            id: 'plateTypeEditorContainer.resetProbePosititon.notification.success',
          }),
        ),
      );
      if (succesCallback) {
        succesCallback();
      }
    });
  };

export const showPlateCreatorPutLabwareInTrayDialog =
  (successCallback = () => {}, errorHandlerCallback = () => {}, isReteachContent = false, cancelCallback = () => {}) =>
  async (dispatch, getState) => {
    const {configState} = getState();
    const {plateTypeEditorDeckInsert} = configState;

    let title = intl.formatMessage({id: 'plateTypeEditorContainer.putLabwareOnDeck.title'});
    let content = (
      <FormattedHTMLMessage id="plateTypeEditorContainer.putLabwareOnDeck.text" values={{plateTypeEditorDeckInsert}} />
    );
    let ok = intl.formatMessage({id: 'plateTypeEditorContainer.putLabwareOnDeck.firstButton'});
    let cancel = intl.formatMessage({id: 'plateTypeEditorContainer.putLabwareOnDeck.secondButton'});
    let modalSize = 'md';

    if (isReteachContent) {
      title = intl.formatMessage({id: 'modal.reteachPlate.placeLabware.title'});
      content = <FormattedHTMLMessage id="modal.reteachPlate.placeLabware.text" />;
      ok = intl.formatMessage({id: 'modal.reteachPlate.placeLabware.confirmButton'});
      cancel = intl.formatMessage({id: 'modal.reteachPlate.placeLabware.cancelButton'});
      modalSize = 'lg';
    }

    dispatch(
      showModal(
        title,
        content,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            text: ok,
            handlerClick: async () => {
              dispatch(hideModal());
              dispatch(handleResetProbePosition(null, successCallback, errorHandlerCallback));
            },
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            text: cancel,
            handlerClick: async () => {
              dispatch(hideModal());
              // show clean up dialog on cancel reteach
              if (isReteachContent) {
                dispatch(showCleanupDialog(true, false, cancelCallback, null, true));
              }
            },
          },
        ],
        () => {
          dispatch(hideModal());
        },
        'modal-plate-creator-put-object-in-tray',
        true,
        modalSize,
      ),
    );
  };

export const showPlateCreatorClearObjectInTrayDialog =
  (successCallback = () => {}, errorHandlerCallback = () => {}, isReteachContent = false, cancelCallback = () => {}) =>
  async (dispatch) => {
    let title = intl.formatMessage({id: 'plateTypeEditorContainer.clearObject.title'});
    let content = intl.formatMessage({id: 'plateTypeEditorContainer.clearObject.text'});
    let ok = intl.formatMessage({id: 'plateTypeEditorContainer.clearObject.firstButton'});
    let cancel = intl.formatMessage({id: 'plateTypeEditorContainer.clearObject.secondButton'});
    let modalSize = 'md';
    let confirmationCheckbox;

    if (isReteachContent) {
      title = intl.formatMessage({id: 'modal.reteachPlate.confirmation.title'});
      content = <FormattedHTMLMessage id="modal.reteachPlate.confirmation.text" />;
      ok = intl.formatMessage({id: 'modal.reteachPlate.confirmation.confirmButton'});
      cancel = intl.formatMessage({id: 'modal.reteachPlate.placeLabware.cancelButton'});
      modalSize = 'lg';
      confirmationCheckbox = <FormattedHTMLMessage id="modal.reteachPlate.confirmation.checkBoxText" />;
    }

    let response;

    const handlerAfterTouchProbeExtend = async () => {
      dispatch(showLoading(intl.formatMessage({id: 'plateTypeEditorContainer.processing.loading'})));
      dispatch(removeTemporaryState('touchProbeExtendFailed'));

      response = await dispatch(ejectTray());
      dispatch(hideLoading());

      if (response !== FastRobotErrorcode.NoError) {
        errorHandlerCallback(response, handlerAfterTouchProbeExtend);
        return;
      }

      dispatch(
        showPlateCreatorPutLabwareInTrayDialog(successCallback, errorHandlerCallback, isReteachContent, cancelCallback),
      );
    };

    const buttonHandler = async () => {
      dispatch(hideModal());

      response = await dispatch(safeTouchProbeExtend());
      if (response !== FastRobotErrorcode.NoError) {
        dispatch(hideLoading());
        let afterErrorHandlingCallback;

        if (response === FastRobotErrorcode.TouchProbeExtendFailed) {
          afterErrorHandlingCallback = handlerAfterTouchProbeExtend;
        } else if (
          response === FastRobotErrorcode.LOAD_CELL_VALUE_NO_CHANGE ||
          response === FastRobotErrorcode.LOAD_CELL_DRIVER_FAULT ||
          response === FastRobotErrorcode.LOAD_CELL_READ_TIMEOUT
        ) {
          dispatch(setErrorCodeNumber(response));
          dispatch(
            showLoadCellErrorModal(() => dispatch(changeView(ViewMode.LABWARE_MANAGER)), response, buttonHandler),
          );
          return;
        } else {
          afterErrorHandlingCallback = buttonHandler;
        }

        errorHandlerCallback(response, afterErrorHandlingCallback);
      } else {
        handlerAfterTouchProbeExtend();
      }
    };

    dispatch(
      showModal(
        title,
        content,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            text: ok,
            handlerClick: buttonHandler,
            disabledByCheckbox: Boolean(confirmationCheckbox),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            text: cancel,
            handlerClick: () => {
              dispatch(hideModal());
              cancelCallback();
            },
          },
        ],
        () => {
          dispatch(hideModal());
          cancelCallback();
        },
        'modal-plate-creator-clear-object-in-tray',
        true,
        modalSize,
        null,
        confirmationCheckbox,
      ),
    );
  };

export const plateCreatorRecoveryHandler =
  (successCallback, isEstop = false) =>
  (dispatch, getState) => {
    const {plateTypeEditorState} = getState();
    const {currentWizardView, processedStep} = plateTypeEditorState;
    dispatch(hideModal());
    dispatch(
      fastRecovery(() => {
        if (isEstop && currentWizardView === 2 && processedStep === 1) {
          dispatch(setCurrentWizardView(1));
        } else if (!isEstop || (isEstop && currentWizardView === 2 && processedStep === 2)) {
          dispatch(
            showPlateCreatorClearObjectInTrayDialog(successCallback, (fastErrorCode) => {
              dispatch(fastCommonActuatorAPIErrorHandler(fastErrorCode, successCallback));
            }),
          );
        }
      }),
    );
  };

const showCannotExecuteTheOperationModal = () => (dispatch, getState) => {
  const {plateTypeEditorState, configState} = getState();

  dispatch(
    showModal(
      intl.formatMessage({id: 'plateTypeEditorContainer.cannotExecuteTheOperationModal.title'}),
      <FormattedHTMLMessage id="plateTypeEditorContainer.cannotExecuteTheOperationModal.text" />,
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: () => {
            const {wellNumber, wellLabel} = plateTypeEditorState;
            const {plateTypeEditorDeckInsert} = configState;
            dispatch(showLoading());
            dispatch(
              alignToDeckInsertWithOffset(
                plateTypeEditorDeckInsert,
                wellLabel || getWellLabelFromWellNumber(wellNumber),
              ),
            ).then((response) => {
              if (response !== FastRobotErrorcode.NoError) {
                dispatch(fastCommonActuatorAPIErrorHandler(response));
              } else {
                dispatch(
                  showSnackbar(
                    Feedback.SUCCESS,
                    intl.formatMessage({
                      id: 'plateTypeEditorContainer.resetProbePosititon.notification.success',
                    }),
                  ),
                );
              }
              dispatch(hideLoading());
            });
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'plateTypeEditorContainer.cannotExecuteTheOperationModal.firstButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      '',
      false,
    ),
  );
};

const showDiscardChangeModal =
  (targetStep = 0) =>
  (dispatch) => {
    const warningText = (
      <div id="discard-protocol-text">
        <FormattedHTMLMessage id="plateTypeEditorContainer.discardChangeModal.text" />
      </div>
    );
    dispatch(
      showModal(
        intl.formatMessage({id: 'plateTypeEditorContainer.discardChangeModal.title'}),
        warningText,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: () => {
              // decrement step
              if (targetStep) {
                dispatch(setCurrentWizardView(targetStep));
              } else {
                dispatch(decrementCurrentWizardView());
              }

              // save state
              dispatch(saveState());
              dispatch(setPlateTypeEditorEditMode(false));
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'plateTypeEditorContainer.discardChangeModal.firstButton',
            }),
          },
          {
            className: 'btn-lg',
            style: 'secondary',
            handlerClick: () => {
              dispatch(hideModal());
            },
            text: intl.formatMessage({
              id: 'plateTypeEditorContainer.discardChangeModal.secondButton',
            }),
          },
        ],
        () => dispatch(hideModal()),
        '',
        true,
      ),
    );
  };

const doEjectTray = () => async (dispatch) => {
  dispatch(
    showLoading(
      intl.formatMessage({
        id: 'plateTypeEditorContainer.ejectingTray.loading',
      }),
    ),
  );

  const response = await dispatch(ejectTray());

  dispatch(hideLoading());

  if (response !== FastRobotErrorcode.NoError) {
    dispatch(fastCommonActuatorAPIErrorHandler(response));

    return null;
  }

  return true;
};

const resetWizardView = (haveEject) => (dispatch) => {
  if (!haveEject) {
    dispatch(doEjectTray());
  }
  dispatch(resetPlateTypeEditorState());
  dispatch(setPlateTypeEditorEjectTray(true));
};

// TODO show return tips modal when there is from manual reteach
export const handleClickBack =
  (isManualReteach = false) =>
  (dispatch, getState) => {
    const {plateTypeEditorState} = getState();
    const {currentWizardView, editMode} = plateTypeEditorState;

    batch(() => {
      if (isManualReteach) {
        // saveChanges is to save the changes into back end. Back button is no needed.
        dispatch(showModalType(ModalType.RETURN_TIPS_CONFIRMATION, {saveChanges: false}));
      } else {
        if (currentWizardView > 1 && !editMode) {
          // decrement step
          dispatch(decrementCurrentWizardView());

          // save state
          dispatch(saveState());
        }
        if (editMode) {
          showDiscardChangeModal();
        }
      }
    });
  };

export const converPlateTypeEditorStateToJSON = () => (dispatch, getState) => {
  const {plateTypeEditorState} = getState();
  const {
    wellNumber,
    wellLabel,
    wellBottomProfile,
    wellShape,
    plateName,
    partNumber,
    vendor,
    notes,
    height,
    depth,
    width,
    length,
    columnOrigin,
    rowOrigin,
    columnPitch,
    rowPitch,
    capacity,
    workingVolume,
    plateType,
  } = plateTypeEditorState;
  const {id} = plateTypeEditorState;
  const isEdit = id !== null;

  // #region get row and col count
  let RowCount = 0;
  let ColCount = 0;
  switch (wellLabel) {
    case WellLabel.WELL_1: {
      RowCount = 1;
      ColCount = 1;
      break;
    }
    case WellLabel.WELL_8: {
      RowCount = 8;
      ColCount = 1;
      break;
    }
    case WellLabel.WELL_12: {
      RowCount = 1;
      ColCount = 12;
      break;
    }
    case WellLabel.WELL_96: {
      RowCount = 8;
      ColCount = 12;
      break;
    }
    case WellLabel.WELL_384: {
      RowCount = 16;
      ColCount = 24;
      break;
    }
    // in IFC plates, rowcount and colcount doesnt really represents the actual number
    // of rows and columns, but what type of IFC it is
    case WellLabel.WELL_IFC_192_24: {
      RowCount = 192;
      ColCount = 24;
      break;
    }
    case WellLabel.WELL_IFC_96_96: {
      RowCount = 96;
      ColCount = 96;
      break;
    }
    case WellLabel.WELL_IFC_48_48: {
      RowCount = 48;
      ColCount = 48;
      break;
    }
    case WellLabel.WELL_SPEAR_BIO: {
      RowCount = 8;
      ColCount = 12;
      break;
    }
    case WellLabel.WELL_24: {
      RowCount = 4;
      ColCount = 6;
      break;
    }
    case WellLabel.WELL_48: {
      RowCount = 6;
      ColCount = 8;
      break;
    }
    default:
      break;
  }
  // #endregion get row and col count

  // #region get wellSurfaceData
  const wellSurfaceData = {
    Shape: wellShape,
  };
  switch (wellShape) {
    case WellSurfaceDataShape.CIRCLE: {
      wellSurfaceData.Diameter = length;
      break;
    }
    case WellSurfaceDataShape.RECTANGLE: {
      wellSurfaceData.Length = length;
      wellSurfaceData.Width = width;
      break;
    }
    default:
      break;
  }
  // #endregion get wellSurfaceData

  const plateDef = {
    Description: plateName,
    RowCount,
    ColCount,
    RowPitch: rowPitch,
    ColumnPitch: columnPitch,
    FirstWellOffset: {
      X: rowOrigin,
      Y: columnOrigin,
      Z: height,
    },
    Height: height,
    Type: plateType,
    WellSurfaceData: wellSurfaceData,
    WellBottomData: {
      Shape: wellBottomProfile,
      H1: depth,
      // H2: 0,
    },
    WellCapacity: capacity,
    WellNumber: wellNumber,
    WellLabel: wellLabel,
    WorkingCapacity: workingVolume,
    DeadVolume: capacity - workingVolume,
    Notes: notes,
    PartNumber: partNumber,
    Vendor: vendor,
  };
  if (wellBottomProfile === WellBottomProfile.FLAT) {
    plateDef.WellBottomData.WellDepth = depth;
  }

  if (isEdit) {
    plateDef.ID = id;
  }

  return plateDef;
};

export const exitPlateTypeEditor = () => (dispatch, getState) => {
  const {appState} = getState();
  const {prevViewMode} = appState;

  // change view to labware manager, when last view is runtime redirect to explorer view
  if (prevViewMode === ViewMode.RUNTIME) {
    dispatch(changeView(ViewMode.EXPLORER));
  } else if (prevViewMode === ViewMode.RETEACH_PLATE) {
    dispatch(redirectToReteachPlatePage());
  } else {
    dispatch(changeView(ViewMode.LABWARE_MANAGER));
  }

  // reset plateTypeEditorState
  if (prevViewMode !== ViewMode.RETEACH_PLATE) {
    dispatch(resetPlateTypeEditorState());
    dispatch(removeTemporaryState('plateTypeEditorState'));
  }

  // reset ManualReteachPlateState
  dispatch(resetIsManualReteachPage());
  dispatch(resetManualReteachPlateState());

  // save state
  dispatch(saveState());
  dispatch(clearInstrumentProcess());
};

const saveLabwareDefinition =
  (isExitEditor = true) =>
  async (dispatch, getState) => {
    const state = getState();
    const {appState, plateTypeEditorState} = state;
    const isPlateRecognitionEnabled = selectIsPlateRecognitionEnabled(state);

    dispatch(hideModal());
    dispatch(
      showLoading(
        intl.formatMessage({
          id: 'plateTypeEditorContainer.processing.loading',
        }),
      ),
    );
    const convertedState = dispatch(converPlateTypeEditorStateToJSON());

    try {
      console.debug('saving: ');
      console.debug(convertedState);

      const isEdit = Boolean(plateTypeEditorState.id);

      if (isEdit) {
        dispatch(updateLabwareDefinition(convertedState)).then(() => {
          const {prevViewMode} = appState;

          dispatch(hideLoading());
          if (isExitEditor) dispatch(exitPlateTypeEditor());

          if (prevViewMode === ViewMode.LABWARE_MANAGER || prevViewMode === ViewMode.RETEACH_PLATE) {
            dispatch(setLastProcessedId({id: plateTypeEditorState.id, isEdit: true}));
          }

          // get all labware list from back end
          dispatch(refreshLabwareDefinitions());
        });
      } else {
        // save labware definition
        dispatch(createLabwareDefinitions([convertedState])).then((result) => {
          const {prevViewMode} = appState;

          // hide loading
          dispatch(hideLoading());
          if (isExitEditor) dispatch(exitPlateTypeEditor());

          if (prevViewMode === ViewMode.LABWARE_MANAGER) {
            dispatch(setLastProcessedId({id: result?.payload[0]?.id, isEdit: false}));
          }

          if (isPlateRecognitionEnabled) {
            dispatch(registerCachedAndClearScannedData(result?.payload[0]?.id));
          }

          // get all labware list from back end
          dispatch(refreshLabwareDefinitions());
        });
      }
    } catch (error) {
      dispatch(hideLoading());
      console.log('fail to save labware ', error);
    }
  };

// FIXME: change the params into object
const modalCleanUp =
  (isCancelling, isExitEditor, callback, callbackToPrevModal, hideSecondaryButton = false) =>
  (dispatch) => {
    const cleaningAction = (res) => {
      if (res !== FastRobotErrorcode.NoError && res) {
        dispatch(fastCommonActuatorAPIErrorHandler(res));
        dispatch(hideLoading());
        return;
      }

      if (!isCancelling) {
        dispatch(saveLabwareDefinition(isExitEditor));
      } else if (isCancelling && isExitEditor) {
        dispatch(exitPlateTypeEditor());
        return;
      }
      if (callback) callback();
    };

    dispatch(
      showModal(
        intl.formatMessage({
          id: 'plateTypeEditorContainer.takeOutPlate.title',
        }),
        intl.formatMessage({
          id: 'plateTypeEditorContainer.takeOutPlate.text',
        }),
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: async () => {
              dispatch(hideModal());
              dispatch(
                showLoading(
                  intl.formatMessage({
                    id: 'plateTypeEditorContainer.processing.loading',
                  }),
                ),
              );

              const isTouchProbeExtended = await dispatch(isTouchProbeExpanded());

              if (!isTouchProbeExtended) {
                cleaningAction();
              } else {
                dispatch(safeTouchProbeRetract()).then((res) => {
                  dispatch(hideLoading());
                  cleaningAction(res);
                });
              }
            },
            text: intl.formatMessage({
              id: 'plateTypeEditorContainer.takeOutPlate.firstButton',
            }),
          },
          hideSecondaryButton
            ? undefined
            : {
                className: 'btn-lg',
                style: 'secondary',
                handlerClick: () => {
                  dispatch(hideModal());
                  if (callbackToPrevModal) callbackToPrevModal();
                },
                text: intl.formatMessage({
                  id: 'plateTypeEditorContainer.takeOutPlate.secondButton',
                }),
              },
        ].filter(Boolean),
        () => dispatch(hideModal()),
        'modal-cleanup-after-finish',
        true,
      ),
    );
  };

const moveToIdlePosition = () => async (dispatch) => {
  dispatch(moveToIdlePosition()).then((fastErrorCode) => {
    if (fastErrorCode !== FastRobotErrorcode.NoError) {
      dispatch(fastCommonActuatorAPIErrorHandler(fastErrorCode));
    }
  });
};

// FIXME: change the params into object
export const showCleanupDialog =
  (
    isCancelling,
    // eslint-disable-next-line default-param-last
    isExitEditor = true,
    callback,
    callbackToPrevModal,
    hideSecondaryButton = false,
  ) =>
  async (dispatch) => {
    const response = await dispatch(doEjectTray());
    if (!response) {
      return;
    }
    const exitEditor = () => {
      if (isExitEditor) dispatch(exitPlateTypeEditor());
      if (callback) callback();
    };

    const moveToIdleExitEditor = () => {
      moveToIdlePosition();
      if (isExitEditor) exitEditor();
      dispatch(hideLoading());
      if (callback) callback();
    };

    if (isCancelling) {
      dispatch(hideModal());
      dispatch(
        showLoading(
          intl.formatMessage({
            id: 'plateTypeEditorContainer.processing.loading',
          }),
        ),
      );
      await dispatch(isTrayInstalled()).then(async (isTrayInstalledRes) => {
        if (isTrayInstalledRes) {
          dispatch(isTouchProbeExpanded()).then(async (isTouchProbeExpandedRes) => {
            if (!isTouchProbeExpandedRes) {
              moveToIdleExitEditor();
            } else {
              dispatch(hideLoading());
              dispatch(modalCleanUp(isCancelling, isExitEditor, callback, callbackToPrevModal, hideSecondaryButton));
            }
          });
        } else {
          moveToIdleExitEditor();
        }
      });
    } else {
      dispatch(hideLoading());
      dispatch(modalCleanUp(isCancelling, isExitEditor, callback, callbackToPrevModal, hideSecondaryButton));
    }
  };

export const showModalInvalidValue =
  (callback = () => {}) =>
  (dispatch) => {
    dispatch(
      showModal(
        intl.formatMessage({id: 'plateTypeEditorContainer.modal.invalidValue.title'}),
        <FormattedHTMLMessage id="plateTypeEditorContainer.modal.invalidValue.text" />,
        [
          {
            className: 'btn-lg',
            style: 'primary',
            handlerClick: async () => {
              dispatch(hideModal());
              dispatch(
                showLoading(
                  intl.formatMessage({
                    id: 'plateTypeEditorContainer.processing.loading',
                  }),
                ),
              );

              await dispatch(handleResetProbePosition());

              dispatch(hideLoading());
              callback();
            },
            text: intl.formatMessage({id: 'plateTypeEditorContainer.modal.invalidValue.firstButton'}),
          },
        ],
      ),
    );
  };

export const doStartReteachPlate = () => async (dispatch) => {
  const successCallback = () => {
    dispatch(setCurrentWizardView(2));
    dispatch(hideLoading());
    dispatch(saveState());
  };

  const cancelCallback = () => {
    dispatch(clearInstrumentProcess());
  };

  const errorHandler = (fastErrorCode, firstButtonCallback) => {
    const idTextModal =
      fastErrorCode === FastRobotErrorcode.TOUCH_PROBE_COLLISION
        ? 'plateTypeEditorContainer.modal.touchProbeCollisionConfirmation'
        : 'plateTypeEditorContainer.modal.headCollisionConfirmation';

    const secondButtonCallback = () => {
      dispatch(
        showCleanupDialog(true, false, cancelCallback, () =>
          dispatch(showConfirmationModal(idTextModal, firstButtonCallback, secondButtonCallback)),
        ),
      );
    };
    dispatch(fastCommonActuatorAPIErrorHandler(fastErrorCode, firstButtonCallback, secondButtonCallback));
  };

  await dispatch(setInstrumentProcess(InstrumentProcessType.PLATE_DEFINITION));
  dispatch(showPlateCreatorClearObjectInTrayDialog(successCallback, errorHandler, true, cancelCallback));
};

export const startReteachPlate = () => async (dispatch, getState) => {
  const state = getState();
  const {trayCalibrationState} = state;
  const {trayType} = trayCalibrationState;

  dispatch(showLoading());
  const isAllowed = await dispatch(checkAndHandleIsAllowedToStartProcess(InstrumentProcessType.PLATE_DEFINITION));
  const {isTrayReady, errorCode: trayErrorCode} = await dispatch(checkTrayReadiness());
  dispatch(hideLoading());

  if (!isAllowed) return;

  if (!isTrayReady) {
    dispatch(onEnterCalibrationMenuErrorHandler(trayErrorCode, SetTrayModalType.PLATE_TYPE_EDITOR));
    return;
  }

  if (trayType !== TrayType.NORMAL_CADDY && trayType !== TrayType.DOUBLE_CADDY) {
    dispatch(showModalUnsupportedTray(true, ViewMode.PLATE_TYPE_EDITOR));
    return;
  }

  dispatch(doStartReteachPlate());
};

export const handleClickNext = () => async (dispatch, getState) => {
  const state = getState();
  const {currentWizardView, processedStep, wellLabel} = state.plateTypeEditorState;
  const isPlateRecognitionEnabled = selectIsPlateRecognitionEnabled(state);

  if (currentWizardView === 1) {
    if (processedStep > 0) {
      dispatch(incrementCurrentWizardView());
    } else {
      const successCallback = () => {
        // increment step
        dispatch(incrementCurrentWizardView());

        if (processedStep < 1) {
          dispatch(setProcessedStep(1));
        }

        // hide loading
        dispatch(hideLoading());

        // save state
        dispatch(saveState());
      };

      const errorHandler = (fastErrorCode, firstButtonCallback) => {
        const idTextModal =
          fastErrorCode === FastRobotErrorcode.TOUCH_PROBE_COLLISION
            ? 'plateTypeEditorContainer.modal.touchProbeCollisionConfirmation'
            : 'plateTypeEditorContainer.modal.headCollisionConfirmation';

        const secondButtonCallback = () => {
          dispatch(
            showCleanupDialog(
              true,
              false,
              null,
              // callback to previous confirmation modal
              () => dispatch(showConfirmationModal(idTextModal, firstButtonCallback, secondButtonCallback)),
            ),
          );
        };
        dispatch(fastCommonActuatorAPIErrorHandler(fastErrorCode, firstButtonCallback, secondButtonCallback));
      };

      dispatch(showPlateCreatorClearObjectInTrayDialog(successCallback, errorHandler));
    }
  } else if (currentWizardView === 2) {
    dispatch(calculateWellCapacityAndWorkingVolume());
    if (!isPlateRecognitionEnabled) {
      dispatch(handleCheckPlateProperties(wellLabel));
    }
  } else if (currentWizardView === 3) {
    if (isPlateRecognitionEnabled) {
      dispatch(incrementCurrentWizardView());
      dispatch(handleCheckPlateProperties(wellLabel));
      return;
    }
    dispatch(showCleanupDialog());
  } else {
    dispatch(showCleanupDialog());
  }
};

const handleCheckPlateProperties = (wellLabel) => (dispatch) => {
  if (wellLabel === WellLabel.WELL_24 || wellLabel === WellLabel.WELL_48) {
    dispatch(showCheckPlatePropertiesModal());
  }
};

const handleMoveToPlateRecognitionStep = (processedStep) => async (dispatch) => {
  await dispatch(moveToIdlePositionApi());
  dispatch(
    showModal(
      intl.formatMessage({
        id: 'plateTypeEditorContainer.takeOutPlate.title',
      }),
      intl.formatMessage({
        id: 'plateTypeEditorContainer.takeOutPlate.text',
      }),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          handlerClick: async () => {
            dispatch(hideModal());
            dispatch(
              showLoading(
                intl.formatMessage({
                  id: 'plateTypeEditorContainer.processing.loading',
                }),
              ),
            );

            await dispatch(retractTouchProbe(fastCommonActuatorAPIErrorHandler));

            dispatch(moveToNextStep(processedStep));
          },
          text: intl.formatMessage({
            id: 'plateTypeEditorContainer.takeOutPlate.firstButton',
          }),
        },
        {
          className: 'btn-lg',
          style: 'secondary',
          handlerClick: () => {
            dispatch(hideModal());
          },
          text: intl.formatMessage({
            id: 'plateTypeEditorContainer.takeOutPlate.secondButton',
          }),
        },
      ],
      () => dispatch(hideModal()),
      'modal-cleanup-after-finish',
      true,
    ),
  );
};

export const calculateWellCapacityAndWorkingVolume = () => (dispatch, getState) => {
  const state = getState();
  const {id, depth, width, length, wellShape, processedStep} = state.plateTypeEditorState;
  const isEdit = id !== null;
  const isPlateRecognitionEnabled = selectIsPlateRecognitionEnabled(state);

  // #region count wellCapacity and workingVolume
  let volume;
  switch (wellShape) {
    case WellSurfaceDataShape.CIRCLE:
      volume = Math.PI * (length / 2) ** 2 * depth;
      break;
    case WellSurfaceDataShape.RECTANGLE:
      volume = width * length * depth;
      break;
    default:
      console.log('error get wellCapacity and workingVolume wellShape unmapped', wellShape);
      break;
  }
  dispatch(setCapacity(Math.round(volume * 100) / 100));
  dispatch(setWorkingVolume(Math.round(volume * 100) / 100));
  // #endregion count wellCapacity and workingVolume

  // increment step
  if (isPlateRecognitionEnabled) {
    if (isEdit) {
      dispatch(moveToTargetStep(4));
      return;
    }

    dispatch(handleMoveToPlateRecognitionStep(processedStep));
    return;
  }

  dispatch(moveToNextStep(processedStep));
};

const moveToTargetStep = (target) => (dispatch) => {
  dispatch(setCurrentWizardView(target));
  dispatch(hideLoading());
  dispatch(saveState());
};

const moveToNextStep = (processedStep) => (dispatch) => {
  dispatch(incrementCurrentWizardView());
  if (processedStep < 2) {
    dispatch(setProcessedStep(2));
  }
  // hide loading
  dispatch(hideLoading());

  // save state
  dispatch(saveState());
};

export const proceedMeasurement =
  (_startPosition, showCleanupDialogIfError = true, callback = () => {}) =>
  async (dispatch, getState) => {
    const {configState, trayCalibrationState, appState, plateTypeEditorState} = getState();
    const {viewMode} = appState;
    const {plateType, wellLabel} = plateTypeEditorState;

    const plateSurfaceTouchDirection =
      plateType === PlateType.MICROPLATE || (plateType === PlateType.IFC && wellLabel === WellLabel.WELL_IFC_192_24)
        ? 1
        : 2;

    console.log('proceed measuring well');
    dispatch(showLoading('System is Moving. Please Wait...'));
    let startingPosition = _startPosition;

    if (_startPosition === null) {
      startingPosition = await dispatch(getCurrentPosition());
    }

    // #region measure well
    const measureWellResponse = await dispatch(
      measureWell(configState.plateTypeEditorDeckInsert, plateSurfaceTouchDirection, wellLabel),
    );

    dispatch(hideLoading());

    if (measureWellResponse.result === FastRobotErrorcode.ActuatorWaitMotionTimeout) {
      await dispatch(setErrorCodeNumber(measureWellResponse.result));
      dispatch(showCannotExecuteTheOperationModal());
      callback(false);
    } else if (measureWellResponse.result === FastRobotErrorcode.ActuatorEmergencyStop) {
      dispatch(
        fastCommonActuatorAPIErrorHandler(measureWellResponse.result, null, null, async () => {
          await dispatch(
            checkIsTouchProbeExtendedWithModal(async () => {
              if (viewMode === ViewMode.PLATE_TYPE_EDITOR) {
                await dispatch(resetWizardView(false));
              } else if (viewMode === ViewMode.SETUP_WIZARD) {
                await dispatch(doEjectTray());
              }
              callback(false);
            }, true),
          );
        }),
      );
    } else if (measureWellResponse.result === FastRobotErrorcode.INVALID_LABWARE) {
      await dispatch(setErrorCodeNumber(measureWellResponse.result));
      dispatch(showModalInvalidValue(() => callback(false)));
    } else if (
      measureWellResponse.result === FastRobotErrorcode.LOAD_CELL_VALUE_NO_CHANGE ||
      measureWellResponse.result === FastRobotErrorcode.LOAD_CELL_DRIVER_FAULT ||
      measureWellResponse.result === FastRobotErrorcode.LOAD_CELL_READ_TIMEOUT
    ) {
      await dispatch(setErrorCodeNumber(measureWellResponse.result));
      dispatch(
        showLoadCellErrorModal(() => {
          callback(false);
          dispatch(setCurrentWizardView(1));
          dispatch(saveState());
          dispatch(setPlateTypeEditorEditMode(false));
          dispatch(changeView(ViewMode.LABWARE_MANAGER));
        }, measureWellResponse.result),
      );
    } else if (measureWellResponse.result !== FastRobotErrorcode.NoError) {
      const errorHandler = (errorCode) => {
        const idTextModal =
          errorCode === FastRobotErrorcode.TOUCH_PROBE_COLLISION
            ? 'plateTypeEditorContainer.modal.touchProbeCollisionConfirmation'
            : 'plateTypeEditorContainer.modal.headCollisionConfirmation';

        const firstButtonCallback = async () => {
          await dispatch(
            handleResetProbePosition(
              startingPosition,
              async () => {
                await dispatch(proceedMeasurement(startingPosition, showCleanupDialogIfError, callback));
              },
              errorHandler,
            ),
          );
        };

        const secondButtonCallback = () => {
          if (showCleanupDialogIfError) {
            dispatch(
              showCleanupDialog(
                true,
                false,
                () => {
                  dispatch(handleClickBack());
                  dispatch(resetWizardView(true));
                },
                () => dispatch(showConfirmationModal(idTextModal, firstButtonCallback, secondButtonCallback)),
              ),
            );
          }
          callback(false, errorCode);
        };
        dispatch(fastCommonActuatorAPIErrorHandler(errorCode, firstButtonCallback, secondButtonCallback));
      };

      errorHandler(measureWellResponse.result);
    } else {
      const {plateTypeEditorDeckInsert} = configState;
      const {deckInserts} = trayCalibrationState;

      const selectedDeck =
        deckInserts.find((deck) => deck.id === plateTypeEditorDeckInsert) ||
        deckInserts.find((deck) => deck.id === '2');
      const baseHeight = selectedDeck.calibrationData.z;

      const plateHeight =
        Math.round((baseHeight - measureWellResponse.wellMeasurementData.plateSurfacePos) * 100) / 100;
      const wellDepth =
        Math.round(
          (measureWellResponse.wellMeasurementData.wellBottomPos -
            measureWellResponse.wellMeasurementData.plateSurfacePos) *
            100,
        ) / 100;
      const colOrigin = Math.round(measureWellResponse.wellMeasurementData.colOrigin * 100) / 100;
      const rowOrigin = Math.round(measureWellResponse.wellMeasurementData.rowOrigin * 100) / 100;
      const wellWidth = Math.round(measureWellResponse.wellMeasurementData.wellWidth * 100) / 100;
      const wellLength = Math.round(measureWellResponse.wellMeasurementData.wellLength * 100) / 100;
      dispatch(setHeight(plateHeight));
      dispatch(setDepth(wellDepth));
      dispatch(setColumnOrigin(colOrigin));
      dispatch(setRowOrigin(rowOrigin));
      dispatch(setWidth(wellWidth));
      dispatch(setLength(wellLength));
      dispatch(setTroughDepth(wellDepth - 0.5));
      dispatch(saveState());

      dispatch(
        showSnackbar(
          Feedback.SUCCESS,
          intl.formatMessage({
            id: 'plateTypeEditorContainer.measureWell.notification.success',
          }),
        ),
      );

      const preparationHeight =
        Math.round(
          (measureWellResponse.wellMeasurementData.plateSurfacePos -
            configState.plateTypeEditorZPositionFromPlateSurface) *
            100,
        ) / 100;
      console.log(
        'measureWellResponse.wellMeasurementData.plateSurfacePos:',
        measureWellResponse.wellMeasurementData.plateSurfacePos,
      );
      console.log(
        'configState.plateTypeEditorZPositionFromPlateSurface:',
        configState.plateTypeEditorZPositionFromPlateSurface,
      );
      console.log('preparationHeight:', preparationHeight);
      const moveVerticalCarrierResponse = await dispatch(safeMoveVerticalCarrier(preparationHeight));

      if (moveVerticalCarrierResponse.result !== FastRobotErrorcode.NoError) {
        dispatch(fastCommonActuatorAPIErrorHandler(moveVerticalCarrierResponse.result));
      }

      callback(true);
    }
    // #endregion measure well

    dispatch(hideLoading());
  };

export const isPlateEditorUpdated = () => (dispatch, getState) => {
  const {plateTypeEditorState, temporaryState} = getState();
  return !isEqual(plateTypeEditorState, temporaryState?.plateTypeEditorState);
};

// #region Manual Reteach Plate
export const generateRowColTipCount = (selectedWell) => {
  let rowTipCount = 0;
  let colTipCount = 0;
  const lstRow = [];
  const lstColumn = [];
  let labwareIndex = 0;

  Object.keys(selectedWell).forEach((key) => {
    const arrSplit = key.split('-');
    labwareIndex = Number(arrSplit[2]);

    if (lstRow.length === 0 && lstColumn.length === 0) {
      lstRow.push(arrSplit[0]);
      lstColumn.push(arrSplit[1]);
      rowTipCount += 1;
      colTipCount += 1;
    } else {
      if (lstRow.filter((i) => i === arrSplit[0])?.length === 0) {
        lstRow.push(arrSplit[0]);
        rowTipCount++;
      }

      if (lstColumn.filter((i) => i === arrSplit[1])?.length === 0) {
        lstColumn.push(arrSplit[1]);
        colTipCount++;
      }
    }
  });

  return {row: Math.min(...lstRow), col: Math.min(...lstColumn), rowTipCount, colTipCount, labwareIndex};
};
// #endregion Manual Reteach Plate

export const showCheckPlatePropertiesModal = () => (dispatch) => {
  dispatch(
    showModal(
      intl.formatMessage({id: 'labwareManager.modal.checkPlateProperties.title'}),
      intl.formatMessage({id: 'labwareManager.modal.checkPlateProperties.text'}),
      [
        {
          className: 'btn-lg',
          style: 'primary',
          text: intl.formatMessage({id: CommonButtonLabel.GOT_IT}),
          handlerClick: async () => {
            dispatch(hideModal());
          },
        },
      ],
    ),
  );
};

export const resetPlateEditorStateOnChangeView = () => (dispatch, getState) => {
  const {plateTypeEditorState} = getState();
  const isEditingExistingPlateType = Boolean(plateTypeEditorState.id);

  dispatch(resetIsManualReteachPage());
  dispatch(resetManualReteachPlateState());

  if (!isEditingExistingPlateType) {
    dispatch(resetPlateTypeEditorState());
    dispatch(removeTemporaryState('plateTypeEditorState'));
  }
};
